'use client'

import { ElementType, ReactNode, useId, useMemo } from 'react'

import { generateMarginCSS } from './lib/generateMarginCSS'

import styles from './Margin.module.scss'

export type Breakpoints = `xxxs` | `xxs` | `xs` | `sm` | `md` | `lg` | `xl` | `xxl` | `xxxl` | `xxxxl`

export type MarginMultiplier = `${number}x`
export type ExtendedMarginMultiplier = Partial<Record<Breakpoints, MarginMultiplier>>

export type MarginProps = {
	mt?: MarginMultiplier | ExtendedMarginMultiplier
	mr?: MarginMultiplier | ExtendedMarginMultiplier
	mb?: MarginMultiplier | ExtendedMarginMultiplier
	ml?: MarginMultiplier | ExtendedMarginMultiplier
	children: ReactNode
	as?: ElementType | keyof JSX.IntrinsicElements | never
}

/**
 * Component-wrapper for margin.
 * @param
 * @param props
 * @param props.mt - margin-top
 * @param props.mr - margin-right
 * @param props.mb - margin-bottom
 * @param props.ml - margin-left
 * @example
 * // Default multiply value equal 8
 * // 4x = 32px, 2x = 16px, 5x = 40px etc.
 * // mt="4x" => margin-top: 32px;
 *       <Margin ml="4x" mt={{ xs: `2x`, lg: `5x`}} >
 *         <div />
 *       </Margin>
 */
export const Margin = ({ mt = `0x`, mr = `0x`, mb = `0x`, ml = `0x`, children, as }: MarginProps): JSX.Element => {
	const id = useId()
	const uid = useMemo(() => `margin-${id.replace(/:/g, '')}`, [id])

	const style: Record<string, string> = {}
	let dynamicCss = ''

	const setVar = (varName: string, value: string) => {
		style[`--${varName}`] = value
	}

	const handleValue = (value: any, direction: string) => {
		if (!value) return
		if (typeof value === 'string') {
			const px = parseInt(value, 10) * 8
			setVar(direction, `${px}px`)
		} else {
			// generate responsive CSS rules scoped to this instance
			dynamicCss += generateMarginCSS(
				value,
				direction === 'mt' ? 'top' : direction === 'mr' ? 'right' : direction === 'mb' ? 'bottom' : 'left',
				uid
			)
		}
	}

	handleValue(mt, 'mt')
	handleValue(mr, 'mr')
	handleValue(mb, 'mb')
	handleValue(ml, 'ml')

	const Component: any = as || 'div'

	return (
		<>
			<Component className={`${styles.root} ${uid}`} style={style}>
				{children}
			</Component>
			{dynamicCss ? <style dangerouslySetInnerHTML={{ __html: dynamicCss }} /> : null}
		</>
	)
}
